import React, {useEffect, useState} from 'react';
import {parseDateFromYYYYMMDD, formatDateToYYYYMMDD, formatTime} from "../Common/Helpers";
import { Blocks } from "../Common/Interfaces";
import ToggleSwitch from "@/components/Common/ToggleSwitch";
import HelpTooltip from "@/components/Common/HelpTooltip";
import {getCookie, setCookie} from "@/components/Common/Cookies";
import {format} from "date-fns";

interface CalendarSideProps {
    blocks: Blocks[];
    selectedDay:  Date | null;
    isVisible: boolean;
    nonWorkingDays: string[];
}

const Sidebar: React.FC<CalendarSideProps> = ({ blocks, selectedDay, isVisible, nonWorkingDays = [] }) => {
    const [filteredBlocks, setFilteredBlocks] = useState<Blocks[]>(blocks);
    const [showAvailableOnly, setShowAvailableOnly] = useState(false);

    // Convert date strings to Date objects
    const blocksWithDateObjects = blocks.map(block => {
        const formattedStartTime = formatTime(block.start_time);
        const formattedEndTime = formatTime(block.end_time);

        return {
            ...block,
            date: typeof block.date === 'string' ? parseDateFromYYYYMMDD(block.date) : block.date,
            start_time: formattedStartTime,
            end_time: formattedEndTime
        };
    });

    const parseTimeTo24Hour = (time: string): number => {
        const [timePart, modifier] = time.split(" "); // Split time into hours:minutes and AM/PM
        let [hours, minutes] = timePart.split(":").map(Number); // Split hours and minutes

        if (modifier === "PM" && hours !== 12) {
            hours += 12; // Convert PM hours to 24-hour format
        }
        if (modifier === "AM" && hours === 12) {
            hours = 0; // Midnight case
        }
        return hours * 60 + minutes; // Return total minutes for easier sorting
    };

    const selectedDayBlocks = filteredBlocks
        .filter(block => {
            const blockDate = block.date instanceof Date ? block.date : new Date(block.date);
            return blockDate.toDateString() === selectedDay?.toDateString();
        })
        .sort((a, b) => {
            const timeA = a.start_time ? parseTimeTo24Hour(a.start_time) : 0;
            const timeB = b.start_time ? parseTimeTo24Hour(b.start_time) : 0;
            return timeA - timeB;
        });


    const isNonWorkingDay = selectedDay ? nonWorkingDays.includes(formatDateToYYYYMMDD(selectedDay)): false;

    const handleToggle = () => {
        const newValue = !showAvailableOnly;
        setShowAvailableOnly(newValue);
        setCookie('showAvailableOnly', newValue.toString(), 365); // Set cookie for 7 days
        filterBlocks(newValue);
    };

    /**
     * Filter blocks based on availability when toggle is active
     *
     * @param showAvailable
     */
    const filterBlocks = (showAvailable: boolean) => {
        if (showAvailable) {
            // Filter out unavailable blocks
            const availableBlocks = blocksWithDateObjects.filter(block => block.availability);
            setFilteredBlocks(availableBlocks);
        } else {
            // Reset to original blocks when toggled off
            setFilteredBlocks(blocksWithDateObjects);
        }
    };

    /**
     * Sync filtered blocks with blocks prop whenever blocks change
     */
    useEffect(() => {
        const cookieValue = getCookie('showAvailableOnly');

        if (cookieValue) {
            const parsedValue = cookieValue === 'true';
            setShowAvailableOnly(parsedValue);
            filterBlocks(parsedValue); // Apply the filter based on the cookie
        } else {
            setFilteredBlocks(blocksWithDateObjects); // Initialize filtered blocks with all blocks
        }
    }, [blocks]); // Update whenever blocks prop changes

    if (!isVisible || !selectedDay) {
        return null;
    }

    return (
        <div className="calendar-side">
            <div className="tab-content">
                <div className="schedule-tab">
                    <h4 className="heading">{format(selectedDay, "EEE d MMMM yyyy")}</h4>
                    {selectedDayBlocks.length > 0 ? (
                        <>
                            <div className="toggler">
                                <ToggleSwitch
                                    toggleName={'show-available'}
                                    isOn={!showAvailableOnly}
                                    handleToggle={handleToggle}
                                    label={"only show available"}
                                />
                                <label className="toggle-text" htmlFor="non-working-toggle">
                                    {showAvailableOnly ? "Only show available" : "Show all blocks"}
                                </label>
                            </div>
                        </>
                    ) : (
                        <p>No availability for the selected day.</p>
                    )}
                    <div className={`block-tab ${isNonWorkingDay ? 'block-tab--disabled' : ''}`}>
                        {selectedDayBlocks.map(block => (
                            <div key={block.id}
                                 className={`single-block ${block.availability ? 'available' : 'unavailable'} ${isNonWorkingDay ? 'single-block--disabled' : ''}`}>
                                <div className="block-content">
                                    <h3>{block.title}</h3>
                                    {block.description && <p>{block.description}</p>}
                                    <div className="block-content__time">
                                        <span>{block.start_time}</span> - <span>{block.end_time}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Sidebar;
